import { toClubCodeId } from '../config/codes.js';
import { signUpTotalPrice } from '../event.js';
import { toTicket, toTicketKey } from '../ticket.js';
import { clone, toNumber } from '../utils/utils.js';
import { cartFromV0 } from './cart.repo.v0.js';
import { toPaymentProvider } from '../payment.js';
import { DateTime } from 'luxon';
const cartSetSessionReserved = (cart, orderId, now) => {
    cart.session.state = 'reserved';
    cart.session.lastChanged = now.toUTC().toISO() || '';
    cart.session.message = '';
    cart.session.orderId = orderId;
    cart.session.accessCodes = [];
    // Reset payment fields to prevent race condition where frontend polling
    // starts before updatePaymentStatus completes. This can happen when a user
    // cancels an order and immediately creates a new one - the old paymentProvider
    // would trigger polling too early.
    cart.session.paymentProvider = 'not-set';
    cart.session.paymentState = 'not-started';
    cart.session.paymentId = '';
};
const cartNewAsBooked = (cart, accessCodes, confirmationText, now) => {
    const newCart = emptyCart();
    newCart.session = clone(cart.session);
    newCart.session.state = 'booked';
    newCart.session.lastChanged = now.toUTC().toISO() || '';
    newCart.session.message = '';
    newCart.session.accessCodes = accessCodes;
    newCart.session.confirmationText = confirmationText;
    // Stamp meta too. The client wipes carts whose meta.lastChanged is old, and
    // a booked cart written without it used to read as brand new forever - so an
    // abandoned confirmation (tab closed instead of dialog dismissed) kept its
    // access codes and resurfaced them on a later visit.
    newCart.meta.lastChanged = now.toUTC().toISO() || '';
    return newCart;
};
const cartSetSessionError = (cart, message, now) => {
    cart.session.state = 'failed';
    cart.session.lastChanged = now.toUTC().toISO() || '';
    cart.session.message = message;
    cart.session.accessCodes = [];
};
const cartSetSessionCancelled = (cart, paymentState, now, cancelReason) => {
    cart.session.state = 'cancelled';
    cart.session.paymentState = paymentState;
    cart.session.lastChanged = now.toUTC().toISO() || '';
    cart.session.message = cancelReason;
    cart.session.orderId = '';
    cart.session.accessCodes = [];
};
const cartSetSessionOpen = (cart) => {
    cart.session = newSession();
    delete cart.meta.orderOnBehalfOf;
};
/**
 * Check if a cart session is stale (reserved for too long without completing).
 * A session is considered stale if it's in 'reserved' state and lastChanged
 * is older than the threshold (default 5 minutes).
 */
const isSessionStale = (session, thresholdMinutes = 5) => {
    if (session.state !== 'reserved')
        return false;
    if (!session.lastChanged)
        return true; // No timestamp = definitely stale
    const lastChanged = DateTime.fromISO(session.lastChanged);
    if (!lastChanged.isValid)
        return true; // Invalid timestamp = stale
    const now = DateTime.now();
    const diffMinutes = now.diff(lastChanged, 'minutes').minutes;
    return diffMinutes > thresholdMinutes;
};
/**
 * How long after a session transition the client may still show the booking
 * confirmation dialog. Shared so the dialog and the cart writer agree on when
 * a 'booked' session has been abandoned.
 */
const CONFIRMATION_DIALOG_MINUTES = 2;
/**
 * Check if a 'booked' session has been left behind - the user closed the tab
 * instead of dismissing the confirmation dialog, so the session was never
 * acknowledged. Such a session must not be carried into a later visit: it
 * still holds the access codes and confirmation text of the finished order.
 */
const sessionIsAbandonedBooked = (session, now) => {
    if (session.state !== 'booked')
        return false;
    if (!session.lastChanged)
        return true; // No timestamp = definitely abandoned
    const lastChanged = DateTime.fromISO(session.lastChanged);
    if (!lastChanged.isValid)
        return true; // Invalid timestamp = abandoned
    return now.diff(lastChanged, 'minutes').minutes > CONFIRMATION_DIALOG_MINUTES;
};
const newSession = () => {
    return {
        state: 'open',
        paymentState: 'not-started',
        paymentProvider: 'not-set',
        paymentId: '',
        lastChanged: '',
        message: '',
        orderId: '',
        accessCodes: [],
        groupByDefault: false,
        extrasByDefault: {},
        confirmationText: ''
    };
};
const emptyCart = () => {
    return {
        tickets: {},
        couponsToBuy: {},
        couponGiftInfo: {},
        couponsToApply: {},
        punchCardsToBuy: [],
        events: [],
        clubCodes: [],
        meta: {},
        session: newSession(),
        version: 3
    };
};
const cartIsEmpty = (cart) => {
    let stuff = Object.values(cart.tickets).length;
    stuff += Object.values(cart.couponsToBuy).length;
    stuff += Object.values(cart.events).length;
    stuff += cart.punchCardsToBuy.length;
    return stuff === 0;
};
const cartTicketSum = (ticket) => {
    const extraSum = ticket.extras
        .map((extra) => toNumber(extra.price, 0))
        .reduce((sum, i) => sum + i, 0);
    return (ticket.unitPrice.finalPrice +
        extraSum +
        (ticket.groupBracket && ticket.groupBracket.length > 0
            ? toNumber(ticket.groupPrice, 0)
            : 0));
};
const cartSum = (cart) => {
    const ticketsPrice = Object.values(cart.tickets)
        .map((ticket) => toNumber(ticket.totalPrice))
        .reduce((sum, i) => sum + i, 0);
    const couponsToBuy = Object.values(cart.couponsToBuy)
        .map((coupon) => toNumber(coupon))
        .reduce((sum, i) => sum + i, 0);
    const couponsToApply = Object.values(cart.couponsToApply)
        .map((coupon) => toNumber(coupon))
        .reduce((sum, i) => sum + i, 0);
    const punchCardsPrice = cart.punchCardsToBuy.reduce((sum, card) => sum + toNumber(card.definition.price), 0);
    const events = signUpTotalPrice(cart.events);
    const sumPreDeductions = ticketsPrice + couponsToBuy + events + punchCardsPrice;
    return sumPreDeductions - Math.min(couponsToApply, sumPreDeductions);
};
const toCartSession = (parent) => {
    const raw = parent.session || {};
    const session = newSession();
    session.state = raw.state;
    session.lastChanged = raw.lastChanged ?? '';
    session.message = raw.message ?? '';
    session.paymentId = raw.paymentId ?? '';
    session.accessCodes = raw.accessCodes ?? [];
    session.groupByDefault = !!raw.groupByDefault;
    session.extrasByDefault = raw.extrasByDefault ?? {};
    session.orderId = raw.orderId ?? (raw.lastSessionId || parent.orderId || '');
    session.paymentProvider = toPaymentProvider(raw.paymentProvider ?? (parent.paymentProvider || 'not-set'));
    session.confirmationText = raw.confirmationText ?? '';
    return session;
};
const toCartDoc = (doc) => {
    const cart = emptyCart();
    if (!doc || Object.values(doc).length === 0) {
        return cart;
    }
    if (!doc.version || doc.version < 1) {
        return cartFromV0(doc);
    }
    const rawTickets = Object.values(doc.tickets ?? {});
    const tickets = rawTickets.map((raw) => [
        toTicketKey(raw.ticketKey),
        toTicket(raw, raw.ticketKey)
    ]);
    cart.tickets = Object.fromEntries(tickets);
    cart.session = toCartSession(doc);
    cart.couponsToBuy = doc.couponsToBuy ?? cart.couponsToBuy;
    cart.couponGiftInfo = doc.couponGiftInfo ?? cart.couponGiftInfo;
    cart.couponsToApply = doc.couponsToApply ?? cart.couponsToApply;
    cart.meta = doc.meta ?? cart.meta;
    cart.punchCardsToBuy = doc.punchCardsToBuy ?? cart.punchCardsToBuy;
    cart.events = doc.events ?? cart.events;
    // Copies of club codes, some taken while their ids were still indexes.
    cart.clubCodes = Array.isArray(doc.clubCodes)
        ? doc.clubCodes.map((code) => ({
            ...code,
            id: toClubCodeId(code.id)
        }))
        : cart.clubCodes;
    cart.session = doc.session ?? cart.session;
    return cart;
};
var CartTicketAction;
(function (CartTicketAction) {
    CartTicketAction["SHOW_INFO"] = "SHOW_INFO";
    CartTicketAction["ADD_TICKET"] = "ADD_TICKET";
    CartTicketAction["REMOVE_TICKET"] = "REMOVE_TICKET";
})(CartTicketAction || (CartTicketAction = {}));
/**
 * The booker's own reference for an order ("Fritekst" in the cart). It is
 * printed on the receipt and mail and sent as the payment provider's order
 * description, so it is kept short and to plain text.
 */
const ORDER_REFERENCE_MAX_LENGTH = 20;
// Letters (any language), digits, space, and common punctuation.
const ORDER_REFERENCE_PATTERN = /^[\p{L}\p{N}\s._\-,.:;!?()]+$/u;
/** Why a reference cannot be used, or null if it can. Empty is fine. */
const orderReferenceProblem = (reference) => {
    if (!reference)
        return null;
    if (reference.length > ORDER_REFERENCE_MAX_LENGTH)
        return 'tooLong';
    if (!ORDER_REFERENCE_PATTERN.test(reference))
        return 'invalid';
    return null;
};
/**
 * The reference to put on an order made from this cart. The cart is
 * client-written, so the client's own check proves nothing here: an invalid
 * reference is dropped rather than failing the booking over a label.
 */
const orderReferenceFromCart = (cart) => {
    const reference = (cart.meta?.reference ?? '').trim();
    return orderReferenceProblem(reference) ? '' : reference;
};
export { toCartDoc, emptyCart, cartIsEmpty, cartSum, cartTicketSum, cartSetSessionReserved, cartNewAsBooked, cartSetSessionError, cartSetSessionCancelled, cartSetSessionOpen, isSessionStale, sessionIsAbandonedBooked, CONFIRMATION_DIALOG_MINUTES, CartTicketAction, ORDER_REFERENCE_MAX_LENGTH, orderReferenceFromCart, orderReferenceProblem };
//# sourceMappingURL=cart.js.map